/**
 * The bar that sits above every page: the Sparkie wordmark and the appearance
 * toggle, and nothing else. There is one destination on this site, so a nav
 * menu would be links to nowhere.
 *
 * The wordmark is a **brand link, not a heading** (FR-019(e)). Each page owns
 * exactly one `<h1>` — the hero headline on `/`, the not-found heading on the
 * not-found page — and this header renders on both, so a heading here would
 * make that two on every page. It is an anchor rather than a styled press
 * target so it behaves like a link: focusable without help, openable in a new
 * tab, and announced as a link.
 *
 * The toggle is a single cycling control; `@/lib/scheme-toggle` explains that
 * choice and holds the logic behind it. Everything it needs comes from the
 * `useThemeMode` context in `theme-provider.tsx`, which owns persistence and
 * the OS subscription — this file only reads the mode and asks for the next.
 */

import type { ComponentType, CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";
import { IconButton, Text } from "react-native-paper";
import {
  MoonGlyph,
  SunGlyph,
  SystemGlyph,
  type GlyphProps,
} from "@/components/icons";
import { useThemeMode } from "@/components/theme-provider";
import type { ThemeMode } from "@/lib/resolve-scheme";
import { nextThemeMode, schemeToggleLabel } from "@/lib/scheme-toggle";
import { useAppTheme } from "@/theme/paper-theme";
import { iconSize, radius, spacing } from "@/theme/tokens";

/**
 * The header's content lines up with the page's own column (T049, §2), so the
 * wordmark sits directly above the hero rather than out at the window edge.
 * Exported so the two cannot drift apart.
 */
export const CONTENT_MAX_WIDTH = 1100;

/**
 * The glyph shows the mode that is in force, not the one a press would select
 * — it reports state, and the accessible name says where a press goes. They
 * are two halves of the same answer, which is why the label is built from the
 * same `mode` this lookup uses.
 *
 * The glyphs are decorative: the button carries the name (FR-019(b)).
 */
const MODE_GLYPHS: Record<ThemeMode, ComponentType<GlyphProps>> = {
  light: SunGlyph,
  dark: MoonGlyph,
  system: SystemGlyph,
};

/**
 * The anchor's own box, in DOM styles rather than a react-native-web
 * `StyleSheet`, because this one element really is an `<a>`. The padding is
 * what the browser's focus ring draws around, so it is what keeps the ring off
 * the letterforms; `inline-flex` stops the anchor from inheriting the
 * inline-baseline gap under its child.
 */
const BRAND_LINK_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  borderRadius: radius.sm,
  padding: spacing.xxs,
};

export function SiteHeader() {
  const theme = useAppTheme();
  const { mode, setMode } = useThemeMode();
  const ModeGlyph = MODE_GLYPHS[mode];

  return (
    <View
      role="banner"
      style={[styles.bar, { borderBottomColor: theme.colors.outlineVariant }]}
    >
      <View style={styles.row}>
        <Link to="/" aria-label="Sparkie, home" style={BRAND_LINK_STYLE}>
          <Text variant="titleLarge" style={{ color: theme.colors.onSurface }}>
            Sparkie
          </Text>
        </Link>

        <IconButton
          mode="outlined"
          size={iconSize.md}
          icon={({ size, color }) => <ModeGlyph size={size} color={color} />}
          accessibilityLabel={schemeToggleLabel(mode)}
          onPress={() => {
            setMode(nextThemeMode(mode));
          }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    // The rule runs the full width of the window while the content stays in
    // the page's column, so the header reads as a band rather than a box.
    alignItems: "center",
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  row: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.md,
    justifyContent: "space-between",
    maxWidth: CONTENT_MAX_WIDTH,
    width: "100%",
  },
});
